import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import Separator from "components/Separator";
import ModalInfinityList from "components/ModalInfinityList";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { liveSearchInput } from "utils";

import { useCities } from "clients/apis";
import Button from "components/Button";

const unregisteredItem = {
  id: null,
  title: strings.unregistered,
  name: strings.unregistered,
  text: strings.unregistered,
};

let timer: ReturnType<typeof setTimeout>;

type City = { id: number; text?: string };

type Props = {
  value: Array<City>;
  onApply: (cities: Array<City>) => void;
};

const CityMultiSelector: React.FC<Props> = (props) => {
  const { value, onApply } = props;

  const [name, setName] = useState("");
  const {
    isError,
    error: citiesError,
    isRefetching,
    refetch,
    isLoading: citiesFetching,
    data: cities,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCities({ name });

  const { flg: modal, on: show, off: hide } = useFlag();
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const _onHide = () => {
    setName("");
    hide();
  };

  const [valueState, setValueState] = useState<Array<City>>([]);

  useEffect(() => {
    setValueState(value);
  }, [value]);

  const _onApply = () => {
    onApply(valueState);
    hide();
  };

  const _renderItem = (items: Array<any>) => {
    return items.map((city: City, key: number) => {
      const itemIndex = valueState.findIndex((e) => e.id === city.id);
      const active = itemIndex > -1;
      const _onPress = () => {
        active
          ? setValueState(valueState?.filter((e) => e.id !== city.id))
          : setValueState((prev) => [...prev, city]);
      };

      return (
        <React.Fragment key={String(key)}>
          <TouchableOpacity
            style={{
              ...styles.item,
              backgroundColor: active ? colors.theme : colors.white,
            }}
            onPress={_onPress}
          >
            <Text color={active ? colors.white : colors.text.normal}>
              {city.text}
            </Text>
            {active && (
              <View style={{ position: "absolute", left: 10 }}>
                <Icon type="antDesign" name={"close"} color={colors.white} />
              </View>
            )}
          </TouchableOpacity>
          <Separator size={"80%"} />
        </React.Fragment>
      );
    });
  };

  const _renderList = () => {
    if (citiesFetching || typing) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return (
        <Error error={citiesError} reset={refetch} loading={isRefetching} />
      );
    }

    if (cities?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty message={strings.no_city} />;
    }

    return (
      <>
        {_renderItem([unregisteredItem])}
        <AnimatedInfinityList
          onRefresh={refetch}
          responseData={cities}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          renderItemList={_renderItem}
        />
        <Button
          square={45}
          absolute
          bottom={10}
          left={10}
          onPress={_onApply}
          icon={{ type: "antDesign", name: "check" }}
        />
      </>
    );
  };

  return (
    <>
      <>
        <TouchableOpacity
          onPress={show}
          activeOpacity={sizes.tapOpacity}
          style={{ ...styles.inputContainer }}
        >
          <View style={styles.iconContainer}>
            <Icon name="chevron-down" />
          </View>
          <View style={styles.textContainer}>
            <Text>{strings.multi_select_city_description}</Text>
          </View>

          <View style={styles.iconContainer}>
            <Icon name="map-pin" />
          </View>
        </TouchableOpacity>
      </>

      <ModalInfinityList close={_onHide} visible={modal}>
        <View style={styles.searchInputContainer}>
          <Input
            style={styles.searchInput}
            hideError
            icon={{ name: "search" }}
            placeholder={strings.search}
            onChangeText={(inputName) => {
              liveSearchInput(
                inputName,
                name,
                setName,
                timer,
                startTyping,
                stopTyping
              );
              refetch();
            }}
          />
        </View>
        {_renderList()}
      </ModalInfinityList>
    </>
  );
};

export default React.memo(CityMultiSelector);
